import React, { useState, useEffect } from 'react';
import {
  Select,
  Button,
  Modal,
  Input,
  message,
  Spin,
  Empty,
  Avatar,
  Typography,
} from 'antd';
import { PlusOutlined, UserOutlined, SearchOutlined } from '@ant-design/icons';
import { Patient } from '../../types/patient';
import { patientService } from '../../services/patientService';
import PatientForm from './PatientForm';

const { Option } = Select;
const { Text } = Typography;

interface PatientSelectorProps {
  selectedPatientId?: number | null;
  selectedPatientName?: string;
  onPatientSelect: (patient: Patient | null, patientName: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
}

const PatientSelector: React.FC<PatientSelectorProps> = ({
  selectedPatientId,
  selectedPatientName,
  onPatientSelect,
  placeholder = "Search and select a patient",
  allowClear = true,
  size = 'middle',
}) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatientId && patients.length > 0) {
      const patient = patients.find(p => p.id === selectedPatientId);
      if (patient) {
        setSelectedPatient(patient);
      }
    }
  }, [selectedPatientId, patients]);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const data = await patientService.getPatients();
      setPatients(data);
    } catch (error) {
      console.error('Error fetching patients:', error);
      message.error('Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  };

  const handlePatientSelect = (value: number) => {
    const patient = patients.find(p => p.id === value);
    if (patient) {
      setSelectedPatient(patient);
      onPatientSelect(patient, patient.name);
    }
  };

  const handleClear = () => {
    setSelectedPatient(null);
    onPatientSelect(null, '');
  };

  const handleAddNewPatient = () => {
    setIsModalVisible(true);
  };

  const handlePatientSaved = () => {
    fetchPatients();
    setIsModalVisible(false);
    message.success('Patient added successfully');
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const getInitials = (name: string) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchText.toLowerCase()) ||
    patient.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    patient.medical_record_number?.toLowerCase().includes(searchText.toLowerCase())
  );

  const renderPatientOption = (patient: Patient) => (
    <div className="flex items-center space-x-3 py-1">
      <Avatar size="small" className="bg-blue-500">
        {getInitials(patient.name)}
      </Avatar>
      <div className="flex-1">
        <div className="font-medium text-sm">{patient.name}</div>
        <div className="text-xs text-gray-500">
          {patient.medical_record_number ? `MRN: ${patient.medical_record_number}` : 'No MRN'}
          {patient.email && ` • ${patient.email}`}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Select
        value={selectedPatient ? selectedPatient.id : undefined}
        placeholder={placeholder}
        size={size}
        allowClear={allowClear}
        showSearch
        loading={loading}
        onSelect={handlePatientSelect}
        onClear={handleClear}
        onSearch={setSearchText}
        filterOption={false}
        notFoundContent={
          loading ? (
            <div className="flex items-center justify-center py-4">
              <Spin size="small" />
              <span className="ml-2">Loading patients...</span>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No patients found"
              className="py-4"
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddNewPatient}
                size="small"
              >
                Add New Patient
              </Button>
            </Empty>
          )
        }
        dropdownRender={(menu) => (
          <div>
            {menu}
            <div className="border-t border-gray-200 p-2">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddNewPatient}
                className="w-full"
                size="small"
              >
                Add New Patient
              </Button>
            </div>
          </div>
        )}
        className="w-full"
      >
        {filteredPatients.map((patient) => (
          <Option key={patient.id} value={patient.id}>
            {renderPatientOption(patient)}
          </Option>
        ))}
      </Select>

      {/* Add New Patient Modal */}
      <Modal
        title="Add New Patient"
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        destroyOnClose
      >
        <PatientForm
          onSave={handlePatientSaved}
          onCancel={handleModalClose}
        />
      </Modal>
    </>
  );
};

export default PatientSelector; 